import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from "@angular/core";
import { MonacoEditorModule } from "ngx-monaco-editor-v2";
import type { editor as MonacoEditor } from "monaco-editor";
import { applyEditorMarkers, EditorMarker } from "../monaco-editor/editor-lint";

/**
 * Reusable Monaco side-by-side diff editor.
 *
 * Content is injected imperatively via setValue() to avoid Angular
 * change-detection overhead with large text payloads.
 *
 * Usage:
 *   <app-monaco-diff-editor
 *     [originalCode]="serverContent"
 *     [(modifiedCode)]="editedContent"
 *     language="yaml"
 *     height="60vh"
 *   />
 */
@Component({
  selector: "app-monaco-diff-editor",
  templateUrl: "./monaco-diff-editor.component.html",
  styleUrl: "./monaco-diff-editor.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MonacoEditorModule],
})
export class MonacoDiffEditorComponent {
  readonly originalCode = input.required<string>();
  /** Two-way bindable: parent reads edits, can also reset by changing originalCode. */
  readonly modifiedCode = model<string>("");
  readonly language = input<string>("plaintext");
  readonly height = input<string>("500px");
  /** Lint markers rendered as squiggles on the modified (editable) side. */
  readonly markers = input<EditorMarker[]>([]);

  protected readonly placeholderModel = computed(() => ({
    code: "",
    language: this.language(),
  }));

  protected editorOptions = {
    theme: "vs-dark",
    renderSideBySide: true,
    automaticLayout: true,
    scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10 },
  };

  private editorInstance: MonacoEditor.IStandaloneDiffEditor | null = null;
  private pendingContent: string | null = null;
  private readonly editorReady = signal(false);

  constructor() {
    effect(() => {
      const original = this.originalCode();
      if (this.editorInstance) {
        this.applyContent(original);
      } else {
        this.pendingContent = original;
      }
    });

    effect(() => {
      const markers = this.markers();
      if (this.editorReady()) {
        applyEditorMarkers(this.editorInstance.getModifiedEditor().getModel(), markers);
      }
    });
  }

  protected onEditorInit(editor: MonacoEditor.IStandaloneDiffEditor): void {
    this.editorInstance = editor;

    if (this.pendingContent !== null) {
      this.applyContent(this.pendingContent);
      this.pendingContent = null;
    }

    editor.getModifiedEditor().onDidChangeModelContent(() => {
      this.modifiedCode.set(editor.getModifiedEditor().getValue());
    });
    this.editorReady.set(true);
  }

  private applyContent(content: string): void {
    this.editorInstance.getOriginalEditor().setValue(content);
    this.editorInstance.getModifiedEditor().setValue(content);
    this.modifiedCode.set(content);
  }
}
